import { Link } from 'react-router-dom';
import { Users, Medal, Heart, Leaf, MapPin, Sparkles, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const FeaturedProducts = () => {
  const impactStats = [
    {
      id: 1,
      icon: <Users className="w-6 h-6" />,
      value: 'Growing Community',
      label: 'Active Artisans',
      chip: 'from-clay-100 to-clay-200 text-clay-600',
      bar: 'from-clay-500 to-clay-600',
    },
    {
      id: 2,
      icon: <Medal className="w-6 h-6" />,
      value: 'Generations Old',
      label: 'Traditional Crafts',
      chip: 'from-saffron-100 to-saffron-200 text-saffron-700',
      bar: 'from-saffron-500 to-saffron-600',
    },
    {
      id: 3,
      icon: <Heart className="w-6 h-6" />,
      value: 'Thousands Happy',
      label: 'Customers & Counting',
      chip: 'from-rose-100 to-rose-200 text-rose-600',
      bar: 'from-rose-500 to-rose-600',
    },
    {
      id: 4,
      icon: <Leaf className="w-6 h-6" />,
      value: '100%',
      label: 'Authentic Handmade',
      chip: 'from-green-100 to-green-200 text-green-700',
      bar: 'from-green-500 to-green-600',
    },
  ];

  const artisanStories = [
    {
      id: 1,
      name: 'Mita Das',
      craft: 'Kantha Embroidery Artisan',
      location: 'Shantiniketan, Bengal',
      story: 'Learning Kantha from my grandmother, I now teach this ancient art to women in my village, keeping our heritage alive.',
      image: 'https://images.unsplash.com/photo-1607748178563-6a7c6f66937b?w=400&h=400&fit=crop',
      legacy: 'Fourth generation artisan',
      impact: 'Mentoring next generation',
    },
    {
      id: 2,
      name: 'Ratan Pal',
      craft: 'Terracotta Potter',
      location: 'Bankura, Bengal',
      story: 'Fifth generation potter, creating the iconic Bankura horses while experimenting with contemporary designs.',
      image: 'https://images.unsplash.com/photo-1603415526960-f7e0328c63b1?w=400&h=400&fit=crop',
      legacy: 'Family tradition since 1890',
      impact: 'Reviving ancient techniques',
    },
    {
      id: 3,
      name: 'Swapna Bauri',
      craft: 'Dokra Metal Craft',
      location: 'Bikna, Bengal',
      story: 'Reviving the ancient lost-wax casting technique, now teaching this 4,000-year-old art to young women in her community.',
      image: 'https://images.unsplash.com/photo-1596815064286-45a8b9f7e8dc?w=400&h=400&fit=crop',
      legacy: 'Preserving tribal heritage',
      impact: 'Empowering women artisans',
    },
    {
      id: 4,
      name: 'Bikash Chitrakar',
      craft: 'Patachitra Artist',
      location: 'Midnapore, Bengal',
      story: 'Telling mythological stories through traditional scroll paintings, carrying forward a lineage of visual storytellers.',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop',
      legacy: 'Traditional scroll painters',
      impact: 'Keeping folk art alive',
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-cream-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 text-rose-600 rounded-full text-sm font-semibold mb-4">
            <Heart className="w-4 h-4" />
            Empowering Artisans, Preserving Heritage
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-cocoa-900">
            Meet Our Artisan Community
          </h2>
          <p className="text-cocoa-600 max-w-2xl mx-auto text-lg">
            Every product carries a story of tradition, skill, and dedication passed down through generations
          </p>
        </motion.div>

        {/* Impact Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
          {impactStats.map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.08 }}
              className="group relative overflow-hidden bg-white rounded-2xl border border-cream-200 shadow-sm hover:shadow-warm transition-all duration-300 hover:-translate-y-1 flex flex-col items-center justify-center text-center p-6 min-h-[170px]"
            >
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${stat.bar}`}></div>
              <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${stat.chip} rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-sm`}>
                {stat.icon}
              </div>
              <div className="text-xl md:text-2xl font-bold text-cocoa-900 mb-1.5">
                {stat.value}
              </div>
              <p className="text-sm text-cocoa-600">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Artisan Stories - Temporarily commented out
        <div className="mb-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-cocoa-900 mb-2">
                The Hands Behind the Craft
              </h3>
              <p className="text-cocoa-600">
                Meet a few of the talented artisans we work with
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {artisanStories.map((artisan, index) => (
              <motion.div
                key={artisan.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.08 }}
                className="group bg-white rounded-2xl border border-cream-200 overflow-hidden hover:shadow-warm hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={artisan.image}
                    alt={artisan.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-cocoa-950/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-4">
                    <span className="bg-white/90 backdrop-blur-md text-clay-600 text-xs font-semibold px-3 py-1 rounded-full">
                      {artisan.craft}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h4 className="text-lg font-bold text-cocoa-900 mb-1">{artisan.name}</h4>
                  <div className="flex items-center text-cocoa-500 text-sm mb-3">
                    <MapPin className="w-4 h-4 mr-1.5 shrink-0" />
                    {artisan.location}
                  </div>
                  <p className="text-cocoa-600 text-sm leading-relaxed mb-4">
                    "{artisan.story}"
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-clay-50 text-clay-600 text-xs font-semibold px-3 py-1 rounded-full">
                      {artisan.legacy}
                    </span>
                    <span className="bg-cream-100 text-cocoa-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {artisan.impact}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        */}

        {/* Mission Banner */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden bg-gradient-to-br from-cocoa-900 via-clay-700 to-saffron-600 rounded-3xl p-8 md:p-12 text-white text-center"
        >
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-saffron-400/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-rose-400/20 rounded-full blur-3xl"></div>

          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/15 backdrop-blur-md border border-white/25 rounded-full mb-5">
              <Heart className="w-8 h-8 text-rose-200" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Our Promise to Artisans
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto text-cream-100/90">
              Every purchase directly supports artisan families, preserves ancient crafts,
              and ensures these traditions live on for future generations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/shop"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-clay-700 rounded-full font-semibold hover:bg-cream-100 hover:-translate-y-0.5 transition-all duration-300"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Shop with Purpose
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-7 py-3 bg-white/10 backdrop-blur text-white rounded-full font-semibold hover:bg-white/20 transition-all duration-300 border border-white/40"
              >
                Learn Our Story
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-12"
        >
          <span className="inline-flex items-center gap-2 text-cocoa-600 mb-4">
            <Sparkles className="w-4 h-4 text-saffron-500" />
            Be part of our journey to preserve Bengal's artistic heritage
          </span>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/register"
              className="px-7 py-3 bg-clay-500 text-white rounded-full font-semibold hover:bg-clay-600 hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Join as Artisan
            </Link>
            <Link
              to="/contact"
              className="px-7 py-3 border-2 border-cream-300 text-cocoa-700 rounded-full font-semibold hover:border-clay-500 hover:text-clay-600 transition-colors"
            >
              Partner With Us
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
